/*
* Pipeline Theme
*
* Use this file to add custom Javascript to Pipeline.  Keeping your custom
* Javascript in this fill will make it easier to update Pipeline. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*
*/


(function() {

  const selectors = {
    discount: '[data-cart-discount]',
    discountForm: '[data-discount-form]',
    discountInput: '[data-discount-input]',
    discountApply: '[data-discount-apply]',
    discountTags: '[data-discount-tags]',
    discountRemove: '[data-discount-remove]',
    formErrors: '[data-form-errors]',
    apiContent: '[data-api-content]'
  };

  function getDiscountRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      || (window.theme && window.theme.routes && window.theme.routes.root_url)
      || '/';
  }

  function getCartUrl() {
    return (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  }

  function getCartUpdateUrl() {
    return `${getCartUrl()}/update.js`;
  }

  function fetchCart() {
    return fetch(`${getCartUrl()}.js`, {
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Could not load cart');
      }
      return response.json();
    });
  }

  function showDiscountError(container, message) {
    const errors = container.closest('[data-cart-bottom]')?.querySelector(selectors.formErrors)
      || document.querySelector(selectors.formErrors);

    if (!errors) return;

    errors.innerHTML = `<p>${message}</p>`;
    errors.style.display = 'block';
    window.setTimeout(() => {
      errors.style.display = 'none';
    }, 4000);
  }

  function refreshDiscountTags(container) {
    const tagsContainer = container.querySelector(selectors.discountTags);
    if (!tagsContainer) return;

    fetch(`${getDiscountRoot()}?section_id=api-cart-discount`)
      .then((response) => response.text())
      .then((response) => {
        const fresh = document.createElement('div');
        fresh.innerHTML = response;
        const content = fresh.querySelector(selectors.apiContent);

        if (!content) return;

        tagsContainer.innerHTML = content.innerHTML;
        tagsContainer.hidden = tagsContainer.children.length === 0;
      })
      .catch(() => {});
  }

  function normalizeCode(code) {
    return (code || '').trim().toUpperCase();
  }

  function isDiscountApplied(cart, code) {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode || !cart) return false;

    if (Array.isArray(cart.cart_level_discount_applications)) {
      const cartLevelMatch = cart.cart_level_discount_applications.some((discount) => {
        return normalizeCode(discount.title) === normalizedCode
          || normalizeCode(discount.code) === normalizedCode;
      });
      if (cartLevelMatch) return true;
    }

    if (!Array.isArray(cart.items)) return false;

    return cart.items.some((item) => {
      if (Array.isArray(item.discounts)) {
        const itemDiscountMatch = item.discounts.some((discount) => {
          return normalizeCode(discount.title) === normalizedCode;
        });
        if (itemDiscountMatch) return true;
      }

      if (!Array.isArray(item.line_level_discount_allocations)) return false;

      return item.line_level_discount_allocations.some((allocation) => {
        const title = allocation.discount_application && allocation.discount_application.title;
        return normalizeCode(title) === normalizedCode;
      });
    });
  }

  function syncCart(cart) {
    document.dispatchEvent(new CustomEvent('theme:cart:change', {
      detail: { cart },
      bubbles: true
    }));
    document.dispatchEvent(new CustomEvent('theme:cart:reload'));
  }

  function applyDiscountWithUpdateApi(code) {
    return fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: code.trim() })
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Invalid discount code');
      }
      return response.json();
    });
  }

  function applyDiscountWithLegacyUrl(code) {
    return fetch(`${getDiscountRoot()}discount/${encodeURIComponent(code.trim())}`, {
      method: 'GET',
      credentials: 'same-origin'
    }).then((response) => {
      if (!response.ok) {
        throw new Error('Invalid discount code');
      }
      return fetchCart();
    });
  }

  function applyDiscount(container, code) {
    const applyButton = container.querySelector(selectors.discountApply);

    if (applyButton) applyButton.disabled = true;

    return applyDiscountWithUpdateApi(code)
      .catch(() => applyDiscountWithLegacyUrl(code))
      .then((cart) => {
        if (!isDiscountApplied(cart, code)) {
          throw new Error('Invalid discount code');
        }

        const input = container.querySelector(selectors.discountInput);
        if (input) input.value = '';

        syncCart(cart);
        refreshDiscountTags(container);
      })
      .catch(() => {
        const message = (window.theme && window.theme.strings && window.theme.strings.discountError)
          || 'Enter a valid discount code';
        showDiscountError(container, message);
      })
      .finally(() => {
        if (applyButton) applyButton.disabled = false;
      });
  }

  function clearDiscount(container) {
    function finish(cart) {
      syncCart(cart);
      refreshDiscountTags(container);
    }

    return fetch(getCartUpdateUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ discount: '' })
    })
      .then((response) => response.json())
      .then(finish)
      .catch(() => fetch(`${getDiscountRoot()}discount/clear`, {
        method: 'GET',
        credentials: 'same-origin'
      })
        .then(() => fetchCart())
        .then(finish))
      .catch(() => {
        document.dispatchEvent(new CustomEvent('theme:cart:reload'));
        refreshDiscountTags(container);
      });
  }

  function initCartDiscount(container) {
    const form = container.querySelector(selectors.discountForm);

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();

        const input = container.querySelector(selectors.discountInput);
        const code = input ? input.value.trim() : '';

        if (!code) return;

        applyDiscount(container, code);
      });
    }

    container.addEventListener('click', (event) => {
      const removeButton = event.target.closest(selectors.discountRemove);
      if (!removeButton) return;

      event.preventDefault();
      clearDiscount(container);
    });
  }

  function initCartDiscounts() {
    document.querySelectorAll(selectors.discount).forEach((container) => {
      if (container.dataset.discountInit === 'true') return;
      container.dataset.discountInit = 'true';
      initCartDiscount(container);
    });
  }

  document.addEventListener('DOMContentLoaded', initCartDiscounts);
  document.addEventListener('shopify:section:load', initCartDiscounts);

  document.addEventListener('theme:cart:change', () => {
    document.querySelectorAll(selectors.discount).forEach((container) => {
      refreshDiscountTags(container);
    });
  });

})();

/*
* CITRIN: re-bind swatch cards that were bound before they finished parsing
* (Sept 22 2026).
*
* product-grid-item.liquid's swatch script watches the DOM with a
* MutationObserver so AJAX-inserted cards get bound. During the initial page
* parse that observer also fires while a card is only PARTLY parsed -- the
* <product-grid-item> element exists but its swatch links don't yet.
* bindCard() marks the card bound (card.__citrinSwatchBound = true) BEFORE
* checking for swatch links, finds none, and returns -- so the card is
* permanently skipped and its swatches never preview on hover. On
* /collections/sale-outlet page 2 that hit 2 of 34 swatch cards.
*
* Deferred scripts run after parsing finishes, so every card is complete by
* now: clear the flag on any card that has swatch links but no bound handler,
* and ask the snippet to bind again via its existing citrin:swatch:rebind
* hook. Cards that bound correctly the first time are left alone.
*/
(function() {
  function rebindPartialSwatchCards() {
    var found = false;
    document.querySelectorAll('product-grid-item').forEach(function(card) {
      if (card.__citrinSwatchBound && !card.__citrinApply && card.querySelector('.siblings__link[data-sibling-cutline]')) {
        card.__citrinSwatchBound = false;
        found = true;
      }
    });
    if (found) document.dispatchEvent(new CustomEvent('citrin:swatch:rebind'));
  }

  rebindPartialSwatchCards();
  document.addEventListener('DOMContentLoaded', rebindPartialSwatchCards);
  window.addEventListener('load', rebindPartialSwatchCards);
})();

/* CITRIN (Pipeline 8.2): the "Available online & in-store" line on product pages.
   Pipeline 8.2 redraws the buy box on every size change and doesn't re-run the
   block's own script, so the line got stuck on "Checking availability...".
   This watches for a freshly redrawn line and fills it in again. */
(function(){
  function fill(root){
    root.dataset.citrinInit = 'true';
    var dot = root.querySelector('[data-citrin-dot]');
    var text = root.querySelector('[data-citrin-text]');
    var online = root.dataset.onlineAvailable === 'true';
    var id = root.dataset.variantId;
    function render(store){
      if (!dot || !text) return;
      dot.setAttribute('data-state', (online || store === true) ? 'in' : 'out');
      if (online && store) text.textContent = 'Available online & in-store';
      else if (online) text.textContent = 'Available online';
      else if (store) text.textContent = 'Available in-store only';
      else if (store === false) text.textContent = 'Currently sold out';
      else text.textContent = 'Currently sold out online';
    }
    render(null);
    if (!id) return;
    fetch('/variants/' + id + '/?section_id=api-pickup-availability')
      .then(function(r){ return r.text(); })
      .then(function(html){ if (root.dataset.variantId === id) render(html.indexOf('var(--color-success)') !== -1); })
      .catch(function(){});
  }
  var queued = false;
  function scan(){
    queued = false;
    document.querySelectorAll('[data-citrin-availability]').forEach(function(el){
      if (el.dataset.citrinInit !== 'true' || el.dataset.citrinFor !== el.dataset.variantId) {
        el.dataset.citrinFor = el.dataset.variantId;
        fill(el);
      }
    });
  }
  function start(){
    scan();
    if (!('MutationObserver' in window)) return;
    new MutationObserver(function(){ if (!queued) { queued = true; setTimeout(scan, 60); } })
      .observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-variant-id', 'data-citrin-init'] });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();

/* CITRIN (Pipeline 8.2): two behaviours that used to live at the end of the old theme.js (hand-added there,
   so the theme update dropped them). Carried over unchanged:
   1. After any "Add to cart" that shows the small "added" pop-down, open the cart drawer as soon as the
      cart has updated.
   2. (Removed Nov 4 2026 at the merchant's request) On phones, the first tap on a product card used to be
      swallowed (it only marked the card "tapped" for 5 seconds, a class nothing styles) so shoppers had to tap
      twice to open a product - and the first tap on a card's quick-add button was swallowed too. */
(function(){
  document.addEventListener('theme:cart:popdown', function(){
    var listener = function(){
      var drawer = document.querySelector('[data-drawer="drawer-cart"]');
      if (drawer) drawer.dispatchEvent(new CustomEvent('theme:drawer:open', { bubbles: false }));
      document.removeEventListener('theme:cart:change', listener);
    };
    document.addEventListener('theme:cart:change', listener);
  });

})();

/* CITRIN (Pipeline 8.2): "Complete the set" box on product pages.
   1. The set script moves the box next to Add to Cart. Pipeline 8.2 redraws each product block on every size
      change and puts a fresh, empty copy back in the box's original spot - shoppers saw a blank "Complete the set"
      box with no photos or prices. Remove that copy while the working box is on the page; if the working box is
      ever gone, let the set script start on the new copy instead.
   2. "Your set is in your bag" / "... added to your bag" stayed on screen after the shopper emptied the bag.
      Clear it as soon as the bag has fewer items than when the message appeared. */
(function(){
  var queued = false;
  function tidy(){
    queued = false;
    var all = [].slice.call(document.querySelectorAll('[data-cbp]'));
    if (!all.length) return;
    var live = all.filter(function(c){ return c.__cbp; });
    var fresh = all.filter(function(c){ return !c.__cbp; });
    if (live.length) {
      fresh.forEach(function(c){ c.remove(); });
    } else if (fresh.length) {
      var target = fresh[0];
      fresh.slice(1).forEach(function(c){ c.remove(); });
      target.parentElement.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
    }
  }
  var msgCount = new WeakMap();
  function cartCount(){
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(function(r){ return r.json(); }).then(function(c){ return c.item_count || 0; });
  }
  function watchMessages(){
    document.querySelectorAll('[data-cbp-msg]').forEach(function(m){
      var t = m.textContent || '';
      var isAdded = /in your bag|added to your bag/i.test(t);
      if (isAdded && !msgCount.has(m)) {
        msgCount.set(m, null);
        cartCount().then(function(n){ if (msgCount.has(m)) msgCount.set(m, n); }).catch(function(){});
      } else if (!isAdded && msgCount.has(m)) {
        msgCount.delete(m);
      }
    });
  }
  function recheck(){
    var msgs = [].slice.call(document.querySelectorAll('[data-cbp-msg]')).filter(function(m){ return msgCount.has(m) && msgCount.get(m) != null; });
    if (!msgs.length) return;
    setTimeout(function(){
      cartCount().then(function(n){
        msgs.forEach(function(m){
          if (n < msgCount.get(m)) { m.textContent = ''; m.className = 'cbp__msg'; msgCount.delete(m); }
        });
      }).catch(function(){});
    }, 300);
  }
  function start(){
    tidy(); watchMessages();
    if ('MutationObserver' in window) {
      new MutationObserver(function(){
        if (!queued) { queued = true; setTimeout(function(){ tidy(); watchMessages(); }, 50); }
      }).observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    ['theme:cart:change','theme:cart:reload','cart:refresh'].forEach(function(ev){ document.addEventListener(ev, recheck); });
    document.addEventListener('visibilitychange', function(){ if (document.visibilityState === 'visible') recheck(); });
    window.addEventListener('pageshow', recheck);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
